import React, { useCallback, useEffect, useState } from 'react';
import { autoRechargeService } from '../../api/services/autorecharge.service';
import { AttemptView, AutoRechargeEventDTO, AutoRechargeStatus, MandateMethod, MandateView } from '../../types/autorecharge';
import { formatMoney } from '../../types/billing';
import { formatServerUtcAsIst } from './closeBooksRules';
import {
  AutoRechargeForm,
  attemptStateClass,
  attemptStateLabels,
  estimateCharge,
  formFromStatus,
  gatewayNames,
  mandateStatusClass,
  mandateStatusLabels,
  methodLabels,
  modeLabel,
  returnUrl,
  settingsBody,
  setupButtonLabel,
  suggestedMandateMax,
  validateForm,
  validateMandateMax,
} from './autoRechargeRules';

/**
 * Wallet auto-recharge for ONE organisation: which gateway applies (and why not), the
 * autopay mandate, the trigger and its caps, and the history. Self-contained (takes an
 * organisation id), so the "My billing" page can embed it as well as Credit Accounts.
 */
interface Props {
  organizationId: string;
  /** False: read-only (role without configure rights). The server re-checks either way. */
  editable: boolean;
}

const inputCls =
  'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-60';
const labelCls = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1';
const hintCls = 'text-xs text-gray-500 dark:text-gray-400 mt-1';
const sectionCls = 'border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-3';
const sectionTitleCls = 'text-sm font-semibold text-gray-900 dark:text-white';
const th = 'px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-300 uppercase whitespace-nowrap text-left';
const td = 'px-3 py-2 text-sm text-gray-900 dark:text-gray-100 whitespace-nowrap';
const badge = 'inline-flex px-2 py-0.5 text-xs font-medium rounded-full';
const primaryBtn = 'px-4 py-2 rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50';
const secondaryBtn =
  'px-3 py-2 rounded-md text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50';

const AutoRechargePanel: React.FC<Props> = ({ organizationId, editable }) => {
  const [status, setStatus] = useState<AutoRechargeStatus | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [form, setForm] = useState<AutoRechargeForm | null>(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [method, setMethod] = useState<MandateMethod>('UPI');
  const [mandateMax, setMandateMax] = useState('');
  const [authUrl, setAuthUrl] = useState<string | null>(null);

  const [attempts, setAttempts] = useState<AttemptView[]>([]);
  const [events, setEvents] = useState<AutoRechargeEventDTO[]>([]);

  const adopt = (s: AutoRechargeStatus) => {
    setStatus(s);
    setForm(formFromStatus(s));
    setMethod((prev) => (s.gateway.methods.includes(prev) ? prev : s.gateway.methods[0] || 'CARD'));
  };

  const loadHistory = useCallback(async () => {
    const [a, e] = await Promise.all([
      autoRechargeService.getAttempts(organizationId, 0, 10),
      autoRechargeService.getEvents(organizationId, 0, 20),
    ]);
    if (a.success && a.data) setAttempts(a.data.content);
    if (e.success && e.data) setEvents(e.data.content);
  }, [organizationId]);

  const load = useCallback(async () => {
    const r = await autoRechargeService.getStatus(organizationId);
    if (r.success && r.data) {
      adopt(r.data);
      setLoadError(null);
    } else {
      setLoadError(r.error || 'Failed to load auto-recharge');
    }
    await loadHistory();
  }, [organizationId, loadHistory]);

  useEffect(() => {
    load();
  }, [load]);

  if (loadError) return <p className="text-sm text-red-600 dark:text-red-400">{loadError}</p>;
  if (!status || !form) return <p className="text-sm text-gray-500 dark:text-gray-400">Loading auto-recharge…</p>;

  const g = status.gateway;
  const cur = status.currency;
  const prepaid = status.billingType === 'PREPAID';
  const formError = validateForm(form);
  const charge = estimateCharge(form.amount, status);
  const active: MandateView | undefined = status.mandates.find((m) => m.id === status.activeMandateId);
  const pending: MandateView | undefined = status.mandates.find(
    (m) => m.status === 'PENDING' && m.gatewayAccountId === g.gatewayAccountId,
  );
  const mandateMaxError = validateMandateMax(mandateMax || suggestedMandateMax(charge, g.gateway), g.gateway, charge);

  const set = (patch: Partial<AutoRechargeForm>) => {
    setForm({ ...form, ...patch });
    setMessage(null);
    setError(null);
  };

  const save = async () => {
    if (formError) return;
    setSaving(true);
    setError(null);
    const r = await autoRechargeService.update(organizationId, settingsBody(form));
    setSaving(false);
    if (r.success && r.data) {
      adopt(r.data);
      setMessage('Auto-recharge settings saved.');
      loadHistory();
    } else {
      setError(r.error || 'Failed to save');
    }
  };

  const setUp = async () => {
    const max = mandateMax || suggestedMandateMax(charge, g.gateway);
    if (validateMandateMax(max, g.gateway, charge)) return;
    setSaving(true);
    setError(null);
    const r = await autoRechargeService.createMandate(organizationId, {
      method,
      maxAmount: max ? Number(max) : undefined,
      returnUrl: returnUrl(window.location.origin),
    });
    setSaving(false);
    if (r.success && r.data) {
      setAuthUrl(r.data.authUrl);
      if (r.data.authUrl) window.open(r.data.authUrl, '_blank', 'noopener,noreferrer');
      setMessage(`Authorisation opened at ${gatewayNames[g.gateway]}. The mandate becomes active once the bank confirms it.`);
      load();
    } else {
      setError(r.error || 'Failed to start autopay set-up');
    }
  };

  const revoke = async (m: MandateView) => {
    if (!window.confirm('Revoke this autopay mandate? Auto-recharge stops until a new one is set up.')) return;
    setSaving(true);
    const r = await autoRechargeService.revokeMandate(organizationId, m.id);
    setSaving(false);
    if (r.success) {
      setMessage(r.data?.statusReason || 'Mandate revoked.');
      load();
    } else {
      setError(r.error || 'Failed to revoke');
    }
  };

  const resume = async () => {
    setSaving(true);
    const r = await autoRechargeService.resume(organizationId);
    setSaving(false);
    if (r.success && r.data) {
      adopt(r.data);
      setMessage('Auto-recharge resumed.');
      loadHistory();
    } else {
      setError(r.error || 'Failed to resume');
    }
  };

  return (
    <div className="space-y-4">
      {!prepaid && (
        <p className="text-sm text-gray-600 dark:text-gray-300">Auto-recharge applies to prepaid accounts only.</p>
      )}

      {/* Gateway */}
      <div className={sectionCls} data-testid="ar-gateway">
        <p className={sectionTitleCls}>Payment gateway</p>
        {g.available ? (
          <div className="text-sm text-gray-700 dark:text-gray-200 space-y-1">
            <p>
              <span className="font-medium">{gatewayNames[g.gateway]}</span> · {g.label} ·{' '}
              <span className={g.mode === 'LIVE' ? 'text-green-700 dark:text-green-400' : 'text-amber-700 dark:text-amber-400'}>
                {modeLabel(g.mode)}
              </span>
            </p>
            <p className={hintCls}>
              Collected by {g.issuerName || g.issuerOrganizationId} for {g.country}/{g.currency}.{' '}
              {g.requiresPreDebitNotice
                ? 'Each debit is notified to you at least 24 hours in advance (RBI e-mandate rules); debits above ₹15,000 may need your approval in the bank/UPI app.'
                : 'Your saved card is charged when the balance falls below the threshold; your bank may ask you to authenticate.'}
            </p>
          </div>
        ) : (
          <p className="text-sm text-amber-700 dark:text-amber-400">
            Auto-recharge is not available for this account: {g.reason}
          </p>
        )}
      </div>

      {status.flagged && (
        <div className="rounded-md bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-800 dark:text-red-300 flex items-start justify-between gap-3">
          <span>
            Auto-recharge is paused: {status.flaggedReason}
            {status.flaggedAt ? ` (${formatServerUtcAsIst(status.flaggedAt)})` : ''}
          </span>
          {editable && (
            <button className={secondaryBtn} onClick={resume} disabled={saving}>
              Resume
            </button>
          )}
        </div>
      )}

      {/* Mandate */}
      <div className={sectionCls} data-testid="ar-mandate">
        <p className={sectionTitleCls}>Autopay mandate</p>
        {active ? (
          <div className="flex items-center justify-between gap-3">
            <div className="text-sm text-gray-700 dark:text-gray-200">
              <span className={`${badge} ${mandateStatusClass(active.status)} mr-2`}>{mandateStatusLabels[active.status]}</span>
              {methodLabels[active.method]}
              {active.maxAmount !== null ? ` · up to ${formatMoney(active.maxAmount, active.currency)} per debit` : ''}
            </div>
            {editable && (
              <button className={secondaryBtn} onClick={() => revoke(active)} disabled={saving}>
                Revoke
              </button>
            )}
          </div>
        ) : (
          <p className="text-sm text-gray-600 dark:text-gray-300">No active mandate{pending ? ' — authorisation pending.' : '.'}</p>
        )}
        {pending && (pending.authUrl || authUrl) && (
          <p className="text-sm">
            <a href={(pending.authUrl || authUrl) as string} target="_blank" rel="noopener noreferrer" className="text-indigo-600 dark:text-indigo-400 underline">
              Open the pending authorisation
            </a>
            {editable && (
              <button className="ml-3 text-xs text-gray-500 underline" onClick={() => revoke(pending)} disabled={saving}>
                cancel it
              </button>
            )}
          </p>
        )}
        {editable && g.available && prepaid && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
            {g.methods.length > 1 && (
              <div>
                <label className={labelCls} htmlFor="ar-method">Method</label>
                <select id="ar-method" className={inputCls} value={method} onChange={(e) => setMethod(e.target.value as MandateMethod)}>
                  {g.methods.map((m) => (
                    <option key={m} value={m}>{methodLabels[m]}</option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className={labelCls} htmlFor="ar-mandate-max">Max per debit ({g.currency}, incl. tax)</label>
              <input
                id="ar-mandate-max"
                className={inputCls}
                value={mandateMax}
                placeholder={suggestedMandateMax(charge, g.gateway) || (g.gateway === 'STRIPE' ? 'optional' : '')}
                onChange={(e) => setMandateMax(e.target.value)}
              />
              {mandateMaxError && <p className="text-xs text-red-600 mt-1">{mandateMaxError}</p>}
            </div>
            <div>
              <button className={primaryBtn} onClick={setUp} disabled={saving || !!mandateMaxError}>
                {setupButtonLabel(g.gateway)}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Trigger and caps */}
      <div className={sectionCls} data-testid="ar-settings">
        <p className={sectionTitleCls}>When to recharge</p>
        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
          <input
            type="checkbox"
            id="ar-enabled"
            checked={form.enabled}
            disabled={!editable || !prepaid}
            onChange={(e) => set({ enabled: e.target.checked })}
          />
          Recharge automatically
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className={labelCls} htmlFor="ar-threshold">When available credit falls below ({cur})</label>
            <input id="ar-threshold" className={inputCls} disabled={!editable} value={form.threshold} onChange={(e) => set({ threshold: e.target.value })} />
          </div>
          <div>
            <label className={labelCls} htmlFor="ar-amount">Add this much credit ({cur}, ex-tax)</label>
            <input id="ar-amount" className={inputCls} disabled={!editable} value={form.amount} onChange={(e) => set({ amount: e.target.value })} />
            {charge !== null && (
              <p className={hintCls} data-testid="ar-charge">
                Charged per recharge: {formatMoney(charge, cur)}
                {status.perRecharge?.note ? ` (${status.perRecharge.note})` : ''}
              </p>
            )}
          </div>
          <div>
            <label className={labelCls} htmlFor="ar-max-per-debit">Max per debit ({cur}, incl. tax; blank = mandate's)</label>
            <input id="ar-max-per-debit" className={inputCls} disabled={!editable} value={form.maxPerDebit} onChange={(e) => set({ maxPerDebit: e.target.value })} />
          </div>
          <div>
            <label className={labelCls} htmlFor="ar-daily-cap">Daily cap ({cur}, incl. tax; blank = none)</label>
            <input id="ar-daily-cap" className={inputCls} disabled={!editable} value={form.dailyCap} onChange={(e) => set({ dailyCap: e.target.value })} />
          </div>
          <div>
            <label className={labelCls} htmlFor="ar-attempts">Attempts per day</label>
            <input id="ar-attempts" className={inputCls} disabled={!editable} value={form.maxAttemptsPerDay} onChange={(e) => set({ maxAttemptsPerDay: e.target.value })} />
          </div>
          <div>
            <label className={labelCls} htmlFor="ar-cooldown">Cooldown between attempts (minutes)</label>
            <input id="ar-cooldown" className={inputCls} disabled={!editable} value={form.cooldownMinutes} onChange={(e) => set({ cooldownMinutes: e.target.value })} />
          </div>
        </div>
        <p className={hintCls}>
          Available credit now: {formatMoney(status.availableBalance, cur)}.{' '}
          {status.wouldRechargeNow ? 'A recharge would start on the next run.' : status.notNowReason ? `Not recharging now: ${status.notNowReason}.` : ''}
        </p>
        {status.inFlight && (
          <p className="text-sm text-blue-700 dark:text-blue-300">
            In progress: {attemptStateLabels[status.inFlight.state]}
            {status.inFlight.chargeAfter && status.inFlight.state === 'NOTIFIED'
              ? ` — debit of ${formatMoney(status.inFlight.chargeAmount, status.inFlight.currency)} not before ${formatServerUtcAsIst(status.inFlight.chargeAfter)}`
              : ''}
          </p>
        )}
        {formError && <p className="text-sm text-red-600">{formError}</p>}
        {error && <p className="text-sm text-red-600">{error}</p>}
        {message && <p className="text-sm text-green-700 dark:text-green-400">{message}</p>}
        {editable && (
          <button className={primaryBtn} onClick={save} disabled={saving || !!formError}>
            Save auto-recharge
          </button>
        )}
      </div>

      {/* History */}
      <div className={sectionCls}>
        <p className={sectionTitleCls}>Recharges</p>
        {attempts.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">None yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead>
                <tr>
                  <th className={th}>Started (IST)</th>
                  <th className={th}>State</th>
                  <th className={th}>Credit</th>
                  <th className={th}>Charged</th>
                  <th className={th}>Notice / debit after</th>
                  <th className={th}>Payment</th>
                  <th className={th}>Reason</th>
                </tr>
              </thead>
              <tbody>
                {attempts.map((a) => (
                  <tr key={a.id}>
                    <td className={td}>{formatServerUtcAsIst(a.createdAt)}</td>
                    <td className={td}><span className={`${badge} ${attemptStateClass(a.state)}`}>{attemptStateLabels[a.state]}</span></td>
                    <td className={td}>{formatMoney(a.creditAmount, a.currency)}</td>
                    <td className={td}>{formatMoney(a.chargeAmount, a.currency)}</td>
                    <td className={td}>{a.chargeAfter ? formatServerUtcAsIst(a.chargeAfter) : '-'}</td>
                    <td className={td}>{a.paymentRef || '-'}</td>
                    <td className={td}>{a.failureReason || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className={sectionTitleCls}>Activity</p>
        {events.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">Nothing yet.</p>
        ) : (
          <ul className="space-y-1 text-sm">
            {events.map((e) => (
              <li key={e.id} className={e.alert ? 'text-red-700 dark:text-red-400' : 'text-gray-700 dark:text-gray-300'}>
                {formatServerUtcAsIst(e.createdAt)} · {e.subject}
                {e.toState ? ` → ${e.toState}` : ''} · {e.detail}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default AutoRechargePanel;
